import {
  Box,
  Flex,
  IconButton,
  useColorMode,
  useColorModeValue,
  Stack,
  Button,
  Container,
  Text,
  HStack,
  useDisclosure,
  VStack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Image,
} from '@chakra-ui/react';
import { MoonIcon, SunIcon, HamburgerIcon } from '@chakra-ui/icons';
import { Link as ScrollLink } from 'react-scroll';
import { motion } from 'framer-motion';
import { navItemVariant } from '@/utils/animation';

const MotionBox = motion(Box);

const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <ScrollLink
    to={to}
    spy={true}
    smooth={true}
    offset={-70}
    duration={500}
    style={{ cursor: 'pointer' }}
  >
    <Button
      variant="ghost"
      p={2}
      _hover={{
        color: useColorModeValue('green.500', 'green.200'),
        bg: useColorModeValue('gray.50', 'gray.700')
      }}
    >
      {children}
    </Button>
  </ScrollLink>
);

export default function Navbar() {
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const logoColor = useColorModeValue('green.600', 'green.200');

  const navItems = ['home', 'services', 'experience', 'skills', 'projects', 'contact'];

  return (
    <MotionBox
      bg={bgColor}
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      borderBottom="1px solid"
      borderColor={borderColor}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box maxW="7xl" mx="auto" px={4}>
        <Flex h={16} alignItems="center" justifyContent="space-between">
          <MotionBox
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <HStack
              spacing={2}
              position="relative"
              p={2}
              _hover={{
                transform: 'scale(1.02)',
                transition: 'all 0.2s ease'
              }}
            >
              <Box
                width="38px"
                height="38px"
                borderRadius="full"
                overflow="hidden"
                border="2px solid"
                borderColor={logoColor}
                boxShadow={`0 0 10px ${logoColor}`}
                transition="all 0.3s ease"
                _hover={{
                  transform: 'scale(1.05)',
                  boxShadow: `0 0 15px ${logoColor}`,
                }}
              >
                <Image
                  src="/profileImage.jpg"
                  alt="Danish Ismail"
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
              </Box>
              <Text
                fontSize="xl"
                fontWeight="black"
                bgGradient={`linear(to-r, ${logoColor}, ${textColor})`}
                bgClip="text"
                letterSpacing="tighter"
                display="flex"
                alignItems="center"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-2px',
                  left: '0',
                  width: '100%',
                  height: '2px',
                  bgGradient: `linear(to-r, ${logoColor}, transparent)`,
                }}
              >
                DI
                <Box
                  as="span"
                  fontSize="xs"
                  ml={1}
                  opacity={0.7}
                  fontWeight="medium"
                >
                  .dev
                </Box>
              </Text>
            </HStack>
          </MotionBox>

          <HStack spacing={2} alignItems="center" flex={1} justify="flex-end">
            <HStack
              as={motion.nav}
              spacing={2}
              display={{ base: 'none', md: 'flex' }}
              variants={navItemVariant}
            >
              {navItems.map((item) => (
                <NavLink key={item} to={item}>
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </NavLink>
              ))}
            </HStack>

            <IconButton
              aria-label="Toggle dark mode"
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              variant="ghost"
              _hover={{
                color: useColorModeValue('green.500', 'green.200'),
                bg: useColorModeValue('gray.50', 'gray.700')
              }}
            />

            <IconButton
              display={{ base: 'flex', md: 'none' }}
              onClick={onOpen}
              variant="ghost"
              aria-label="Open menu"
              icon={<HamburgerIcon />}
            />
          </HStack>
        </Flex>
      </Box>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            Menu
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4}>
              {navItems.map((item) => (
                <ScrollLink
                  key={item}
                  to={item}
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={500}
                  onClick={onClose}
                  style={{ width: '100%' }}
                >
                  <Button variant="ghost" w="full">
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </Button>
                </ScrollLink>
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </MotionBox>
  );
}
